import { motion } from "framer-motion";
import { X, User } from "lucide-react";
import { SEGMENTS } from "@/lib/santriSchema";

// Reusable read-only preview of a santri record (grouped by segment)
// `hideKeys` — extra field keys to hide (used by pembimbing to not show income)
export default function SantriPreviewModal({ record, onClose, hideKeys = [], includeCredentials = false }) {
  const hidden = new Set(hideKeys);
  // Always exclude username/password from segment rendering (shown separately when includeCredentials)
  hidden.add("username");
  hidden.add("password");

  const val = (v, type) => {
    if (v === undefined || v === null || String(v).trim() === "") return "—";
    if (type === "date") {
      try {
        const d = new Date(v);
        if (!isNaN(d.getTime())) return d.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
      } catch {}
    }
    return v;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-forest/60 backdrop-blur-sm flex items-center justify-center p-3 md:p-6"
      onClick={onClose}
      data-testid="santri-preview-modal"
    >
      <motion.div
        initial={{ y: 30, opacity: 0, scale: 0.98 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 30, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="bg-parchment rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden border border-gold/40 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold/20">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-forest text-parchment overflow-hidden border-2 border-gold/40 flex items-center justify-center">
              {record.foto_profil ? (
                <img src={record.foto_profil} alt="" className="w-full h-full object-cover" />
              ) : (
                <User size={22} />
              )}
            </div>
            <div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Preview Data Lengkap</span>
              <h3 className="font-serif-display text-forest text-2xl leading-tight">
                {record.nama_santri || record.username || "Santri"}
              </h3>
              <p className="text-[11px] text-moss mt-0.5">
                {record.status_santri || "—"}
                {record.tahun_masuk_grq ? ` • Angkatan ${record.tahun_masuk_grq}` : ""}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-forest hover:text-gold p-2" aria-label="Tutup" data-testid="preview-close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
          {includeCredentials && (
            <section>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1 h-5 bg-gold rounded-full" />
                <h4 className="font-serif-display text-forest text-xl">Kredensial Login</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <PreviewField label="Username (ID)" value={record.username} />
                <PreviewField label="Password" value={record.password} mono />
              </div>
            </section>
          )}

          {SEGMENTS.map((seg) => {
            const fields = seg.fields.filter(
              (f) => !hidden.has(f.key) && f.type !== "computed"
            );
            if (fields.length === 0) return null;
            return (
              <section key={seg.id}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-1 h-5 bg-gold rounded-full" />
                  <h4 className="font-serif-display text-forest text-xl">{seg.label}</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  {fields.map((f) => {
                    if (f.type === "image" && f.key === "foto_profil") {
                      return (
                        <div key={f.key} className="md:col-span-2">
                          <p className="text-[10px] tracking-[0.25em] uppercase text-moss">{f.label}</p>
                          <div className="mt-2 w-28 h-28 rounded-2xl overflow-hidden border border-gold/30 bg-sand flex items-center justify-center">
                            {record.foto_profil ? (
                              <img src={record.foto_profil} alt="Foto" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs text-moss">Tidak ada</span>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <PreviewField
                        key={f.key}
                        label={f.label}
                        value={val(record[f.key], f.type)}
                        wide={f.type === "textarea"}
                      />
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gold/20">
          <button onClick={onClose} className="px-6 py-2.5 rounded-full bg-forest text-parchment text-sm hover:bg-gold transition-colors" data-testid="preview-close-footer">
            Tutup
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function PreviewField({ label, value, wide, mono }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <p className="text-[10px] tracking-[0.25em] uppercase text-moss">{label}</p>
      <p className={`mt-1 text-forest text-sm md:text-base whitespace-pre-wrap ${mono ? "font-mono" : ""}`}>
        {value && String(value).trim() ? value : "—"}
      </p>
    </div>
  );
}
